import { useSyncExternalStore } from 'react'

import type { Maybe } from 'aem-libs-ts/src/types/utility'
import { isPromise } from 'aem-libs-ts/src/utils/common/isPromise'

import { InletStore } from './InletStore'


type SetterResult<State> = State | Promise<State>

export type SetterFn<State, StoreContext> = (state: State, context: StoreContext) => SetterResult<State>

interface Actions<State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: (...args: any[]) => SetterResult<State>
}

interface DefaultActions<State> {
  setState: (state: State) => void
}

type GetPartialState<State> = () => Partial<State>

interface CreateStoreResult<State, StoreContext, ActionsType extends Actions<State>> {
  useStoreData: <SelectorResult = State>(selector?: (values: State) => SelectorResult) => SelectorResult
  useStoreActions: () => DefaultActions<State> & ActionsType
  initialize: (state?: Maybe<Partial<State>>) => Initializer<State, StoreContext>
}

interface CreateStoreDefaultResult<State, StoreContext> {
  useStoreData: <SelectorResult = State>(selector?: (values: State) => SelectorResult) => SelectorResult
  useStoreActions: () => DefaultActions<State>
  initialize: (state?: Maybe<State>) => Initializer<State, StoreContext>
}

interface Initializer<State, StoreContext> {
  map: (mapParams: GetPartialState<State>) => Initializer<State, StoreContext>
  useStoreContext: (mapContext: () => StoreContext) => Initializer<State, StoreContext>
  useQueryParams: (
    mapParams: (searchParams: URLSearchParams) => Maybe<Partial<State>>
  ) => Initializer<State, StoreContext>
  use: () => State
}

type QueryParamsSetFn<State> = (values: State) => Record<string, string>

interface StoreOptions<State> {
  propagateAsQueryParams?: QueryParamsSetFn<State> | false | null
}

export function createInletStore<State>(
  initialState: State | null,
  options?: StoreOptions<State> | null
): CreateStoreDefaultResult<State, unknown>

export function createInletStore<State, StoreContext, ActionsType extends Actions<State>>(
  initialState: State | null,
  options?: StoreOptions<State> | null,
  actions?: (set: (setterFn: SetterFn<State, StoreContext>) => SetterResult<State>) => ActionsType,
  storeContext?: StoreContext
): CreateStoreResult<State, StoreContext, ActionsType>

export function createInletStore<State, StoreContext, ActionsType extends Actions<State>>(
  initialState: State | null,
  options?: StoreOptions<State> | null,
  actions?: (set: (setterFn: SetterFn<State, StoreContext>) => SetterResult<State>) => ActionsType,
  storeContext?: StoreContext
): CreateStoreResult<State, StoreContext, ActionsType> | CreateStoreDefaultResult<State, StoreContext> {
  const store = new InletStore(initialState as State)
  let currentStoreContext = storeContext

  const defaultOptions: StoreOptions<State> = {
    propagateAsQueryParams: false
  }
  const usedOptions: StoreOptions<State> = {
    ...defaultOptions,
    ...(options ?? {})
  }

  const setQueryParams = (newParamsObject: Record<string, string>) => {
    const currentParams = new URLSearchParams(window.location.search)
    Object.entries(newParamsObject).forEach(([key, value]) => {
      currentParams.set(key, value)
    })

    window.history.replaceState(
      Object.fromEntries(currentParams),
      '',
      `${window.location.pathname}${window.location.hash}${
        currentParams.size > 0 ? '?' + currentParams.toString() : ''
      }`
    )
  }

  const getSearchString = () => (window.location.search?.length > 0 ? window.location.search : window.location.hash)

  const initialize = (state: Maybe<State> = null) => {
    let currentState: State = state ?? ({} as State)

    const mapNewState = (mapParams: GetPartialState<State>) => {
      currentState = { ...currentState, ...mapParams() }
      return initializer
    }

    const initializer: Initializer<State, StoreContext> = {
      useQueryParams: (mapSearchParams: (searchParams: URLSearchParams) => Maybe<Partial<State>>) => {
        const locationSearchString = getSearchString()
        const partialState = mapSearchParams(new URLSearchParams(locationSearchString)) ?? ({} as Partial<State>)
        currentState = { ...currentState, ...partialState }
        return initializer
      },
      map: mapNewState,
      useStoreContext: (mapContext: () => StoreContext) => {
        currentStoreContext = mapContext()
        return initializer
      },
      use: () => {
        store.setState(currentState)
        return currentState
      }
    }

    return initializer
  }

  const onStoreUpdate = (newState: State) => {
    if (usedOptions.propagateAsQueryParams) {
      setQueryParams(usedOptions.propagateAsQueryParams(newState))
    }
  }

  const setFn = (setter: SetterFn<State, StoreContext>) => {
    const newState = setter(store.getState(), currentStoreContext as StoreContext)
    if (isPromise(newState)) {
      newState.then((state) => {
        store.setState(state)
        onStoreUpdate(state)
      })

      return newState
    }

    onStoreUpdate(newState)
    store.setState(newState)
    return newState
  }

  return {
    initialize,
    useStoreData: <SelectorResult>(selector = (state: State) => state as unknown as SelectorResult) =>
      useSyncExternalStore(store.subscribe, () => store.getState(selector)),
    useStoreActions: () => {
      const setState = (state: State) => setFn(() => state)

      if (!actions) {
        return { setState, initialize }
      }

      const definedActions = actions(setFn)

      return {
        ...definedActions,
        setState,
        initialize
      }
    }
  }
}
